import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, UserPlus, Plus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Loading, Avatar, EmptyState, Modal } from "../components/ui";

export default function Onboarding() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [empId, setEmpId] = useState("");
  const [busy, setBusy] = useState(false);
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/onboarding").then(({ data }) => setGroups(data));
  useEffect(() => { load(); if (canManage) api.get("/employees?limit=100").then(({ data }) => setEmps(data.items)); }, []);
  const toggle = async (t) => { await api.put(`/onboarding/${t.id}`, { completed: !t.completed }); load(); };
  const createJourney = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/onboarding/journey", { employeeId: empId }); setOpen(false); setEmpId(""); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!groups) return <Loading />;

  return (
    <div>
      <PageHeader title="Onboarding" subtitle="New-hire journeys & task checklists">
        {canManage && <button data-testid="new-journey" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Journey</button>}
      </PageHeader>
      {groups.length === 0 ? <EmptyState icon={UserPlus} title="No onboarding in progress" hint={canManage ? "Start a journey for a new hire." : undefined} /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {groups.map((g) => {
            const byCat = {};
            (g.tasks || []).forEach((t) => { const c = t.category || "General"; (byCat[c] = byCat[c] || []).push(t); });
            return (
              <Card key={g.employeeId} className="p-6" data-testid={`onboarding-${g.employeeId}`}>
                <div className="mb-4 flex items-center gap-3">
                  <Avatar name={g.employeeName} size={42} />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 dark:text-slate-100">{g.employeeName}</p>
                    <p className="text-xs text-slate-400">{g.tasks.filter((t) => t.completed).length}/{g.tasks.length} tasks · {g.progress}% complete</p>
                  </div>
                  <span className="font-head text-2xl font-extrabold text-accent">{g.progress}%</span>
                </div>
                <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent transition-all" style={{ width: `${g.progress}%` }} /></div>
                <div className="space-y-4">
                  {Object.entries(byCat).map(([cat, items]) => (
                    <div key={cat}>
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{cat}</p>
                      <div className="space-y-1">
                        {items.map((t) => (
                          <button key={t.id} onClick={() => toggle(t)} data-testid={`task-${t.id}`} className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            {t.completed ? <CheckCircle2 size={18} className="shrink-0 text-emerald-500" /> : <Circle size={18} className="shrink-0 text-slate-300 dark:text-slate-600" />}
                            <span className={`flex-1 text-sm ${t.completed ? "text-slate-400 line-through" : "text-slate-700 dark:text-slate-200"}`}>{t.task || t.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Start Onboarding Journey"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="jr-form" disabled={busy} className="btn-accent">Create Journey</button></>}>
        <form id="jr-form" onSubmit={createJourney} className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">An 8-step checklist (Documentation, IT Setup, HR Orientation, Workspace, Buddy) will be created for the selected employee.</p>
          <div><label className="label">Employee *</label>
            <select required data-testid="jr-emp" value={empId} onChange={(e) => setEmpId(e.target.value)} className="input">
              <option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
